import React from 'react'
import type { Block } from '../../types'
import type { CodeFrame } from '../../../renderer/blocks/CodeBlock'
import { PropertyLabel, PropertyInput } from './shared'
import { Terminal, Monitor, FileCode } from 'lucide-react'

interface CodeBlockPropertiesProps {
  block: Block
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

const CODE_LANGUAGES = ['javascript', 'python', 'ruby', 'html', 'css', 'bash']

const CODE_FRAMES: { frame: CodeFrame; label: string; icon: React.ReactNode }[] = [
  { frame: 'default', label: 'Default', icon: <FileCode className="h-4 w-4" /> },
  { frame: 'terminal', label: 'Terminal', icon: <Terminal className="h-4 w-4" /> },
  { frame: 'browser', label: 'Browser', icon: <Monitor className="h-4 w-4" /> },
]

export default function CodeBlockProperties({ block, onUpdateBlock }: CodeBlockPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const currentFrame = (config.frame as CodeFrame) || 'default'

  const updateConfig = (updates: Record<string, unknown>) => {
    onUpdateBlock(block.id, { config: { ...config, ...updates } })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <PropertyLabel>Frame</PropertyLabel>
        <div className="grid grid-cols-3 gap-1.5">
          {CODE_FRAMES.map((f) => (
            <button
              key={f.frame}
              onClick={() => updateConfig({ frame: f.frame })}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all text-xs font-medium ${
                currentFrame === f.frame
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {f.icon}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Language</PropertyLabel>
        <select
          value={(config.language as string) || 'javascript'}
          onChange={(e) => updateConfig({ language: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
        >
          {CODE_LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>{lang}</option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Filename</PropertyLabel>
        <PropertyInput
          value={(config.filename as string) || ''}
          onChange={(e) => updateConfig({ filename: e.target.value })}
          placeholder="e.g. app.js"
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>Code</PropertyLabel>
        <textarea
          value={(config.code as string) || ''}
          onChange={(e) => updateConfig({ code: e.target.value })}
          className="w-full bg-slate-900 text-slate-200 border border-slate-700 rounded-lg p-3 text-xs font-mono min-h-32 resize-y focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
          placeholder="Enter code..."
          spellCheck={false}
        />
      </div>
    </div>
  )
}
